import { ReactNode } from 'react';
import { compose } from 'redux';

import { ratioFormatter } from '../columns/count';
import { coloredRatingBar, coloredTotalRating } from '../columns/DOM';
import { scoreSignal } from '../columns/score';
import {
    formatConditionalDigitNumber,
    formatIntegerNumber,
    formatOneDigitNumber,
    formatToValueOrZero,
    formatTwoOrThreeDecimals,
} from './formatters';
import { formatSecondsToTime } from './stringFormatters';
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const columnFormatterMap: Record<string, (value: any) => ReactNode> = {
    fuelConsumption: formatOneDigitNumber,
    electricOperatingConsumption: formatTwoOrThreeDecimals,
    cruiseControlConsumptionInPotential: formatOneDigitNumber,
    electricCruiseControlConsumptionInPotential: formatTwoOrThreeDecimals,
    noCruiseControlConsumptionInPotential: formatOneDigitNumber,
    electricNoCruiseControlConsumptionInPotential: formatTwoOrThreeDecimals,
    operatingFuelConsumption: formatOneDigitNumber,
    electricAverageOperatingConsumption: formatOneDigitNumber,
    drivingConsumption: formatOneDigitNumber,
    electricAverageDrivingConsumption: formatOneDigitNumber,
    distanceFuelConsumption: formatOneDigitNumber,
    electricAverageOperatingConsumptionRange: formatOneDigitNumber,
    idlingConsumption: formatOneDigitNumber,
    electricAverageIdlingConsumption: formatOneDigitNumber,
    fuelEfficiency: formatTwoOrThreeDecimals,
    electricEfficiency: formatTwoOrThreeDecimals,
    electricRecuperation: formatOneDigitNumber,
    drivetrainConsumption: formatOneDigitNumber,
    eptoConsumption: formatOneDigitNumber,
    batterylossesConsumption: formatOneDigitNumber,
    aircompressorConsumption: formatOneDigitNumber,
    coolingConsumption: formatOneDigitNumber,
    hvacConsumption: formatOneDigitNumber,
    vpowersupplyConsumption: formatOneDigitNumber,
    totalPowerConsumption: formatOneDigitNumber,
    electricRecuperationPerKm: formatOneDigitNumber,
    drivetrainConsumptionPerKm: formatOneDigitNumber,
    eptoConsumptionPerKm: formatOneDigitNumber,
    batterylossesConsumptionPerKm: formatOneDigitNumber,
    aircompressorConsumptionPerKm: formatOneDigitNumber,
    coolingConsumptionPerKm: formatOneDigitNumber,
    hvacConsumptionPerKm: formatOneDigitNumber,
    vpowersupplyConsumptionPerKm: formatOneDigitNumber,
    electricRecuperationKmPerKwh: formatOneDigitNumber,
    electricRecuperationPerHour: formatOneDigitNumber,
    averageWeight: formatOneDigitNumber,
    mileage: formatConditionalDigitNumber,
    co2Emission: formatOneDigitNumber,
    averageSpeed: formatOneDigitNumber,
    totalNumberOfDays: formatIntegerNumber,
    operatingDays: formatIntegerNumber,
    drivingTime: formatSecondsToTime,
    idlingTime: formatSecondsToTime,
    operatingTime: formatSecondsToTime,
    activeCoastingTime: formatSecondsToTime,
    passiveCoastingTime: formatSecondsToTime,
    coastingCcTime: formatSecondsToTime,
    kickdownTime: formatSecondsToTime,
    accelerationPedalTimeWithCc: formatSecondsToTime,
    serviceBrakeTime: formatSecondsToTime,
    motorBrakeTime: formatSecondsToTime,
    retarderTime: formatSecondsToTime,
    cruiseControlTimeInPotential: formatSecondsToTime,
    noCruiseControlTimeInPotential: formatSecondsToTime,
    coastingTime: formatSecondsToTime,
    driveProgramActiveTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramEfficiencyTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramEfficiencyPlusTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramEmergencyTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramFluidTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramHeavyDutyTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramManoeuverTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramManualTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramOffroadTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramOffroadWithAssistanceTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramPerformanceTime: compose(formatToValueOrZero, formatSecondsToTime),
    driveProgramTotalTime: compose(formatToValueOrZero, formatSecondsToTime),
    accTime: compose(formatToValueOrZero, formatSecondsToTime),
    noAccTime: compose(formatToValueOrZero, formatSecondsToTime),
    pccaTime: compose(formatToValueOrZero, formatSecondsToTime),
    noPccaTime: compose(formatToValueOrZero, formatSecondsToTime),
    harshAccelerationMediumTime: compose(formatToValueOrZero, formatSecondsToTime),
    harshAccelerationHardTime: compose(formatToValueOrZero, formatSecondsToTime),
    harshBrakingMediumTime: compose(formatToValueOrZero, formatSecondsToTime),
    harshBrakingHardTime: compose(formatToValueOrZero, formatSecondsToTime),
    excessiveIdlingTime: compose(formatToValueOrZero, formatSecondsToTime),
    acceptableIdlingTime: compose(formatToValueOrZero, formatSecondsToTime),
    coastingCcDistance: formatConditionalDigitNumber,
    kickdownDistance: formatConditionalDigitNumber,
    accelerationPedalDistanceWithCc: formatConditionalDigitNumber,
    serviceBrakeDistance: formatConditionalDigitNumber,
    motorBrakeDistance: formatConditionalDigitNumber,
    cruiseControlDistanceInPotential: formatConditionalDigitNumber,
    noCruiseControlDistanceInPotential: formatConditionalDigitNumber,
    cruiseControlPotentialDistance: formatConditionalDigitNumber,
    overspeedDistance: formatConditionalDigitNumber,
    activeCoastingDistance: formatConditionalDigitNumber,
    passiveCoastingDistance: formatConditionalDigitNumber,
    retarderDistance: formatConditionalDigitNumber,
    coastingDistance: formatConditionalDigitNumber,
    driveProgramActiveDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramEfficiencyDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramEfficiencyPlusDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramEmergencyDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramFluidDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramHeavyDutyDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramManoeuverDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramManualDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramOffroadDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramOffroadWithAssistanceDistance: compose(
        formatToValueOrZero,
        formatConditionalDigitNumber,
    ),
    driveProgramPerformanceDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    driveProgramTotalDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    accDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    noAccDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    pccaDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    noPccaDistance: compose(formatToValueOrZero, formatConditionalDigitNumber),
    drivenElevationUp: formatOneDigitNumber,
    drivenElevationDown: formatOneDigitNumber,
    averageSlopeDown: formatIntegerNumber,
    averageSlopeUp: formatIntegerNumber,
    foresightedDrivingPercentage: coloredRatingBar,
    foresightedDrivingPercentageV2: coloredRatingBar,
    coastingRating: coloredRatingBar,
    accelerationPedalRating: coloredRatingBar,
    accelerationPedalRatingV2: coloredRatingBar,
    brakingRating: coloredRatingBar,
    cruiseControlRating: coloredRatingBar,
    cruiseControlRatingV2: coloredRatingBar,
    cruiseControlRatingV3: coloredRatingBar,
    overspeedRating: coloredRatingBar,
    totalRating: coloredRatingBar,
    totalRatingWithoutCC: coloredRatingBar,
    totalRatingV2: coloredRatingBar,
    fuelForesightedDrivingPercentage: coloredTotalRating,
    totalRatingV3: coloredRatingBar,
    totalRatingWithoutCCV2: coloredRatingBar,
    harshAccelerationRating: coloredRatingBar,
    harshBrakingRating: coloredRatingBar,
    excessiveIdlingRating: coloredRatingBar,
    electricRecuperationRating: coloredRatingBar,
    electricAccelerationPedalRating: coloredRatingBar,
    electricOverspeedRating: coloredRatingBar,
    electricCruiseControlRating: coloredRatingBar,
    electricForesightedDrivingPercentage: coloredTotalRating,
    harshAccelerationMediumCount: formatOneDigitNumber,
    harshAccelerationHardCount: formatOneDigitNumber,
    harshBrakingMediumCount: formatOneDigitNumber,
    harshBrakingHardCount: formatOneDigitNumber,
    excessiveIdlingCount: ratioFormatter,
    operationCondition: scoreSignal.format,
};
